import React, { useEffect, useState } from 'react';
import { Typography, Chip, Box, Paper, CardContent, Avatar, Stack, Collapse, IconButton, Badge, Grow, Fade, Slide, Button } from '@mui/material';
import { EnhancedCard } from '../layout/EnhancedCard';
import type { Alert } from '../../store/slices/alertsSlice';
import { alpha } from '@mui/material/styles';

// Icons
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import ComputerIcon from '@mui/icons-material/Computer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const getSeverityConfig = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return {
        color: 'error' as const,
        icon: <ErrorOutlineIcon />,
        bgColor: '#E31B23',
        gradient: 'linear-gradient(135deg, rgba(227, 27, 35, 0.1) 0%, rgba(227, 27, 35, 0.05) 100%)',
        borderColor: '#E31B23',
      };
    case 'high':
      return {
        color: 'warning' as const,
        icon: <WarningAmberIcon />,
        bgColor: '#F39C12',
        gradient: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(243, 156, 18, 0.05) 100%)',
        borderColor: '#F39C12',
      };
    case 'medium':
      return {
        color: 'info' as const,
        icon: <InfoOutlinedIcon />,
        bgColor: '#3498DB',
        gradient: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(52, 152, 219, 0.05) 100%)',
        borderColor: '#3498DB',
      };
    case 'low':
      return {
        color: 'success' as const,
        icon: <CheckCircleOutlineIcon />,
        bgColor: '#27AE60',
        gradient: 'linear-gradient(135deg, rgba(39, 174, 96, 0.1) 0%, rgba(39, 174, 96, 0.05) 100%)',
        borderColor: '#27AE60',
      };
    default:
      return {
        color: 'default' as const,
        icon: <NotificationImportantIcon />,
        bgColor: '#888888',
        gradient: 'linear-gradient(135deg, rgba(136, 136, 136, 0.1) 0%, rgba(136, 136, 136, 0.05) 100%)',
        borderColor: '#888888',
      };
  }
};

const formatRelativeTime = (timestamp: string) => {
  const now = new Date();
  const alertTime = new Date(timestamp);
  const diffMinutes = Math.floor((now.getTime() - alertTime.getTime()) / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
  return `${Math.floor(diffMinutes / 1440)}d ago`;
};

interface AnimatedAlertCardProps {
  alert: Alert;
  index: number;
  onToggleExpand: (id: string) => void;
  isExpanded: boolean;
  onExitComplete: (alertId: string) => void;
  onEnterComplete: (alertId: string) => void;
}

const AnimatedAlertCard: React.FC<AnimatedAlertCardProps> = ({
  alert,
  index,
  onToggleExpand,
  isExpanded,
  onExitComplete,
  onEnterComplete,
}) => {
  const [shouldRender, setShouldRender] = useState(true);
  const [isFlashing, setIsFlashing] = useState(false);
  const severityConfig = getSeverityConfig(alert.severity);

  const animationState = alert.animationState || 'stable';
  const isNewAlert = alert.isNew || false;

  // Handle exit animation
  useEffect(() => {
    if (animationState === 'exiting') {
      const timer = setTimeout(() => {
        setShouldRender(false);
        onExitComplete(alert.id);
      }, 500); // Match exit animation duration

      return () => clearTimeout(timer);
    }
  }, [animationState, alert.id, onExitComplete]);

  // Handle enter animation completion
  useEffect(() => {
    if (animationState === 'entering') {
      const timer = setTimeout(() => {
        onEnterComplete(alert.id);
      }, 600); // Match enter animation duration

      return () => clearTimeout(timer);
    }
  }, [animationState, alert.id, onEnterComplete]);

  // Handle new alert flash effect
  useEffect(() => {
    if (animationState === 'new-flash') {
      setIsFlashing(true);
      const timer = setTimeout(() => {
        setIsFlashing(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [animationState]);

  if (!shouldRender) {
    return null;
  }

  const cardContent = (
    <EnhancedCard
      cardVariant='gradient'
      sx={{
        background: isFlashing
          ? `linear-gradient(135deg, ${alpha(severityConfig.bgColor, 0.25)} 0%, ${alpha(severityConfig.bgColor, 0.15)} 100%)`
          : severityConfig.gradient,
        borderLeft: `4px solid ${severityConfig.borderColor}`,
        cursor: 'pointer',
        transition: 'all 0.3s ease-out',
        ...(isFlashing && {
          boxShadow: `0 0 20px ${alpha(severityConfig.bgColor, 0.5)}`,
          animation: 'flashPulse 1.5s ease-in-out',
          '@keyframes flashPulse': {
            '0%': {
              background: severityConfig.gradient,
              boxShadow: 'none',
            },
            '50%': {
              background: `linear-gradient(135deg, ${alpha(severityConfig.bgColor, 0.3)} 0%, ${alpha(severityConfig.bgColor, 0.2)} 100%)`,
              boxShadow: `0 0 25px ${alpha(severityConfig.bgColor, 0.6)}`,
            },
            '100%': {
              background: severityConfig.gradient,
              boxShadow: 'none',
            },
          },
        }),
        '&:hover': {
          background: `linear-gradient(135deg, ${alpha(severityConfig.bgColor, 0.15)} 0%, ${alpha(severityConfig.bgColor, 0.08)} 100%)`,
        },
      }}
      onClick={() => onToggleExpand(alert.id)}
    >
      {/* Severity indicator glow */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: severityConfig.borderColor,
          boxShadow: `0 0 20px ${alpha(severityConfig.bgColor, 0.6)}`,
          animation: alert.severity === 'critical' ? 'pulse 2s infinite' : 'none',
        }}
      />

      <CardContent sx={{ pb: 1 }}>
        {/* Header with icon, title and severity */}
        <Box display='flex' alignItems='flex-start' gap={2} mb={2}>
          <Avatar
            sx={{
              bgcolor: severityConfig.bgColor,
              color: 'white',
              width: 48,
              height: 48,
              boxShadow: `0 4px 12px ${alpha(severityConfig.bgColor, 0.4)}`,
            }}
          >
            {severityConfig.icon}
          </Avatar>

          <Box flex={1}>
            <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
              <Box display='flex' alignItems='center' gap={1}>
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'text.primary',
                    lineHeight: 1.3,
                  }}
                >
                  {alert.title}
                </Typography>
                {isNewAlert && (
                  <Chip
                    label='NEW'
                    size='small'
                    sx={{
                      backgroundColor: '#FF4444',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.6rem',
                      height: 18,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                        '100%': { opacity: 1 },
                      },
                    }}
                  />
                )}
              </Box>

              <IconButton
                size='small'
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-out',
                  color: severityConfig.bgColor,
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>

            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <Badge
                badgeContent={
                  <Chip
                    label={alert.severity.toUpperCase()}
                    color={severityConfig.color}
                    size='small'
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                }
                sx={{
                  '& .MuiBadge-badge': {
                    position: 'static',
                    transform: 'none',
                  },
                }}
              />

              <Chip
                label={alert.category}
                variant='outlined'
                size='small'
                sx={{
                  borderColor: alpha(severityConfig.bgColor, 0.3),
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              />
            </Box>

            {/* Timestamp with relative time */}
            <Box display='flex' alignItems='center' gap={1}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant='caption' color='text.secondary'>
                {formatRelativeTime(alert.createdAt)} • {new Date(alert.createdAt).toLocaleString()}
              </Typography>
            </Box>

            {/* Hostname display */}
            {(() => {
              // Extract hostname from metadata or title
              let hostname = alert.metadata?.hostname;
              if (!hostname) {
                const match = alert.title.match(/\(([^)]+)\)$/);
                hostname = match ? match[1] : null;
              }

              return hostname ? (
                <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                  <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant='caption' color='text.secondary'>
                    {hostname}
                  </Typography>
                </Box>
              ) : null;
            })()}
          </Box>
        </Box>
      </CardContent>

      {/* Expandable content */}
      <Collapse in={isExpanded}>
        <CardContent sx={{ pt: 0 }}>
          {alert.description && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: alpha('#000', 0.1),
                border: `1px solid ${alpha(severityConfig.bgColor, 0.2)}`,
              }}
            >
              <Typography variant='body2' color='text.primary' sx={{ fontStyle: 'italic' }}>
                "{alert.description}"
              </Typography>
            </Paper>
          )}

          {/* Character and Item info */}
          <Stack direction='row' spacing={2} flexWrap='wrap'>
            {alert.character && (
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: alpha('#2874A6', 0.1),
                  border: `1px solid ${alpha('#2874A6', 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#2874A6' }}>
                  <PersonIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Character
                  </Typography>
                  <Typography variant='body2' fontWeight={500}>
                    {alert.character.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    ID: {alert.character.id}
                  </Typography>
                </Box>
              </Paper>
            )}

            {alert.item && (
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: alpha('#27AE60', 0.1),
                  border: `1px solid ${alpha('#27AE60', 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#27AE60' }}>
                  <InventoryIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Item
                  </Typography>
                  <Typography variant='body2' fontWeight={500}>
                    {alert.item.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    ID: {alert.item.id}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Stack>

          {/* SOP deep-link — rendered only when backend attached a non-empty URL (feature on + mapping present) */}
          {alert.sopUrl && alert.sopUrl.trim() && (
            <Box mt={2}>
              <Button
                size='small'
                variant='outlined'
                href={alert.sopUrl}
                target='_blank'
                rel='noopener noreferrer'
                startIcon={<OpenInNewIcon />}
                onClick={e => e.stopPropagation()}
                sx={{ textTransform: 'none' }}
              >
                {alert.sopLabel?.trim() || 'View SOP \u2192'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </EnhancedCard>
  );

  // Wrap with appropriate animation based on state
  if (animationState === 'entering') {
    return (
      <Slide in={true} direction='up' timeout={{ enter: 600, exit: 0 }} style={{ transitionDelay: `${Math.min(index * 100, 500)}ms` }}>
        <Grow in={true} timeout={{ enter: 600, exit: 0 }} style={{ transitionDelay: `${Math.min(index * 100, 500)}ms` }}>
          <Box>{cardContent}</Box>
        </Grow>
      </Slide>
    );
  }

  if (animationState === 'exiting') {
    return (
      <Fade in={false} timeout={500}>
        <Grow in={false} timeout={500}>
          <Box>{cardContent}</Box>
        </Grow>
      </Fade>
    );
  }

  // For stable and new-flash states, just render normally
  return cardContent;
};

export default AnimatedAlertCard;
